import type { ComponentPropsWithRef, ReactNode } from "react";
import { cx } from "../../lib/cx";
import type { ButtonSize, ButtonVariant } from "../Button/Button";
import buttonStyles from "../Button/Button.module.css";
import { Spinner } from "../Spinner/Spinner";
import { Tooltip, type TooltipProps } from "../Tooltip/Tooltip";
import styles from "./IconButton.module.css";

export interface IconButtonProps
  extends Omit<ComponentPropsWithRef<"button">, "aria-label" | "children"> {
  /** Required: an icon-only control has no visible name. */
  "aria-label": string;
  /** The icon (decorative; hidden from assistive technology). */
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * Wraps the button in a Tooltip. `true` reuses the aria-label as the text.
   * Supplementary only: the aria-label stays the accessible name.
   */
  tooltip?: ReactNode | true;
  tooltipSide?: TooltipProps["side"];
}

/** Square, icon-only button. Same variants and sizes as `Button`; defaults to ghost. */
export function IconButton({
  variant = "ghost",
  size = "md",
  loading = false,
  tooltip,
  tooltipSide,
  className,
  children,
  type = "button",
  onClick,
  ...rest
}: IconButtonProps) {
  const button = (
    <button
      {...rest}
      type={type}
      className={cx(buttonStyles.button, styles.iconButton, className)}
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      onClick={loading ? (event) => event.preventDefault() : onClick}
    >
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <span className={styles.icon} aria-hidden="true">
          {children}
        </span>
      )}
    </button>
  );
  if (tooltip === undefined || tooltip === false || tooltip === null) return button;
  return (
    <Tooltip content={tooltip === true ? rest["aria-label"] : tooltip} side={tooltipSide}>
      {button}
    </Tooltip>
  );
}
